// srtLoaders.ts

import type { SrtEntry, WordEntry } from '@lambda-libs/types';
import { notify } from '../NotificationPlugin';
import {
  parseTranscriptDocument,
  cuesToSrtEntries,
  TranscriptDocumentError,
} from '@lambda-libs/contracts/transcript-document';

// Timed entry parsing backed by the shared transcript-document contract.
// VTT and SRT are parsed as their real formats; plain text / OCR text-only
// JSON are untimed and throw (callers route those to the text viewer instead
// of inventing fake 0-to-N-second cues). Nonempty malformed subtitles throw
// instead of silently resolving to an empty cue list.
export function parseTimedEntries(text: string, fileName?: string): SrtEntry[] {
  let doc;
  try {
    doc = parseTranscriptDocument(text, { fileName });
  } catch (error: any) {
    if (error instanceof TranscriptDocumentError) throw new Error(error.message);
    throw error;
  }
  if (doc.kind !== 'timed') {
    throw new Error('Untimed text transcript: open with the text viewer, not subtitle cues');
  }
  return cuesToSrtEntries(doc.cues) as SrtEntry[];
}

export function isUntimedTextError(error: unknown): boolean {
  return error instanceof Error && /Untimed text transcript/.test(error.message);
}

export interface SrtLoader {
  load(data: any): Promise<SrtEntry[]>;
  unload(entries: SrtEntry[]): Promise<any>;
}

// Transcription payloads vary in the wild: pipeline files nest segments under
// `output` (prediction.json), draft content carries top-level `segments`.
// Anything else degrades to plain-text parsing (usually empty) — so normalize
// here and let callers fall back to text only when truly segment-less.
export function extractSegmentsJson(payload: any): string | null {
  const segs = payload?.output?.segments ?? payload?.segments ?? null;
  if (Array.isArray(segs)) return JSON.stringify({ segments: segs });
  return null;
}

export class ApiSrtLoader implements SrtLoader {
  async load(url: string): Promise<SrtEntry[]> {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }
  async unload(entries: SrtEntry[]): Promise<string> {
    return JSON.stringify(entries, null, 2);
  }
}

export class FileSrtLoader implements SrtLoader {
  async load(file: File): Promise<SrtEntry[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text === 'string') {
          try {
            // Contract-aware: VTT parses as VTT, plain text throws untimed.
            resolve(parseTimedEntries(text, (file as File)?.name));
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('Failed to read file content'));
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  }

  /*
  public parseSrtContent(content: string): SrtEntry[] {
    const entries: SrtEntry[] = [];
    const regex = /(\d+)\s+(\d{2}:\d{2}:\d{2},\d{3})\s-->\s(\d{2}:\d{2}:\d{2},\d{3})\s([\s\S]*?)(?=\n\n|\n*$)/g;
    let match;

    while ((match = regex.exec(content)) !== null) {
      const id = parseInt(match[1]).toString();
      const startTime = this.timeToSeconds(match[2]);
      const endTime = this.timeToSeconds(match[3]);
      const text = match[4].trim();
      const words = text.split(/\s+/);
      
      // Convert times to integers (multiply by 100 for 2 decimal precision)
      const startTimeInt = Math.round(startTime * 100);
      const endTimeInt = Math.round(endTime * 100);
      const durationInt = endTimeInt - startTimeInt;

      // Calculate total characters in the subtitle (excluding spaces)
      const totalChars = text.replace(/\s+/g, '').length;
      
      // Calculate time per character (in integer form)
      const timePerChar = Math.floor(durationInt / totalChars);
      
      // Create array of word objects with calculated timings
      let currentCharPosition = 0;
      const wordObjects = words.map((word) => {
        // Count actual characters in this word (excluding spaces)
        const wordChars = word.length;
        
        // Calculate word timing in integer form
        const wordDurationInt = timePerChar * wordChars;
        const wordStartInt = startTimeInt + (timePerChar * currentCharPosition);
        const wordEndInt = wordStartInt + wordDurationInt;
        
        // Update character position for next word
        currentCharPosition += wordChars;
        
        return {
          word,
          // Convert back to decimal with 2 decimal places
          start: Number((wordStartInt / 100).toFixed(2)),
          end: Number((wordEndInt / 100).toFixed(2))
        };
      });

      entries.push({
        id,
        startTime,
        endTime,
        text,
        lines: text.split('\n').length,
        words: wordObjects
      });
    }
    
    return entries;
  }
  */

  public parseSrtContent(content: string): SrtEntry[] {
    const entries: SrtEntry[] = [];
    
    // Normalize line endings and clean the content
    const normalizedContent = content
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .trim();

    // Match both arrow format and period format
    const subtitleBlockRegex = /(\d+)\n(\d{1,2}:\d{2}:\d{2}[.,]\d{3})\s*(?:-->|\.|period)\s*(\d{1,2}:\d{2}:\d{2}[.,]\d{3})\n([\s\S]*?)(?=\n\d+\n|$)/g;
    
    let match;
    while ((match = subtitleBlockRegex.exec(normalizedContent)) !== null) {
      try {
        const id = match[1];
        const startTimeStr = match[2];
        const endTimeStr = match[3];
        const text = match[4].trim();

        // Handle both arrow and period formats
        const startTime = this.timeToSeconds(startTimeStr);
        const endTime = this.timeToSeconds(endTimeStr);

        // Process words with timing
        const words = text.split(/\s+/);
        const totalChars = text.replace(/\s+/g, '').length;
        
        if (totalChars === 0) continue; // Skip entries with no text
        
        const duration = endTime - startTime;
        const timePerChar = duration / totalChars;
        let currentCharPosition = 0;
        
        const wordObjects = words.map(word => {
          const wordChars = word.length;
          const wordStart = startTime + (timePerChar * currentCharPosition);
          const wordDuration = timePerChar * wordChars;
          currentCharPosition += wordChars;
          return {
            word,
            start: Number(wordStart.toFixed(3)),
            end: Number((wordStart + wordDuration).toFixed(3))
          };
        });

        entries.push({
          id,
          startTime,
          endTime,
          text,
          lines: text.split('\n').length,
          words: wordObjects
        });
      } catch (error) {
        console.error(`Error processing subtitle block: ${match[0]}`, error);
        continue;
      }
    }

    // Sort entries by start time
    entries.sort((a, b) => a.startTime - b.startTime);
    
    return entries;
  }

  private timeToSeconds(timeStr: string): number {
    // Replace period with comma for consistent processing
    timeStr = timeStr.replace('.', ',');
    const [time, millisStr] = timeStr.split(',');
    const [hours, minutes, seconds] = time.split(':').map(Number);
    const millis = Number(millisStr) / 1000;
    
    return hours * 3600 + minutes * 60 + seconds + millis;
  }

  // Helper method to validate timestamp format
  private isValidTimestamp(timeStr: string): boolean {
    const timestampRegex = /^\d{1,2}:\d{2}:\d{2}[.,]\d{3}$/;
    return timestampRegex.test(timeStr);
  }

  // Helper method to format seconds back to SRT timestamp
  public formatSeconds(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const millis = Math.round((seconds % 1) * 1000);
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(millis).padStart(3, '0')}`;
  }

  // Method to serialize entries back to SRT format
  public serialize(entries: SrtEntry[]): string {
    return entries.map((entry, index) => {
      const number = index + 1;
      const timeRange = `${this.formatSeconds(entry.startTime)} --> ${this.formatSeconds(entry.endTime)}`;
      return `${number}\n${timeRange}\n${entry.text}\n`;
    }).join('\n');
  }

  async unload(entries: SrtEntry[]): Promise<string> {
    return entries
      .map((entry, index) => {
        const startTime = this.secondsToTime(entry.startTime);
        const endTime = this.secondsToTime(entry.endTime);
        return `${index + 1}\n${startTime} --> ${endTime}\n${entry.text}\n`;
      })
      .join('\n');
  }

  private secondsToTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds
      .toString()
      .padStart(3, '0')}`;
  }

}

export class TextSrtLoader implements SrtLoader {
  async load(text: string, fileName?: string): Promise<SrtEntry[]> {
    // VTT is timed and parses via its real format; plain text is untimed and
    // throws so callers route to the text viewer instead of fake SRT cues.
    return parseTimedEntries(text, fileName);
  }

  async unload(entries: SrtEntry[]): Promise<string> {
    return entries
      .map((entry, index) => {
        const startTime = this.secondsToTime(entry.startTime);
        const endTime = this.secondsToTime(entry.endTime);
        return `${index + 1}\n${startTime} --> ${endTime}\n${entry.text}\n`;
      })
      .join('\n');
  }

  private secondsToTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds
      .toString()
      .padStart(3, '0')}`;
  }
}

export class JsonSrtLoader implements SrtLoader {
  async load(jsonData: any): Promise<SrtEntry[]> {
    // Shared contract: provider output.segments, direct segments, and explicit
    // empty arrays work; unsupported/malformed JSON throws a meaningful error
    // instead of silently becoming an empty transcript. OCR text-only JSON is
    // untimed and throws so callers use the text viewer.
    let text: string;
    if (typeof jsonData === 'string') text = jsonData;
    else text = JSON.stringify(jsonData);
    let doc;
    try {
      doc = parseTranscriptDocument(text);
    } catch (error: any) {
      const message = error instanceof TranscriptDocumentError ? error.message : 'Invalid JSON format: missing or invalid segments array';
      notify('danger', message);
      throw new Error(message);
    }
    if (doc.kind !== 'timed') {
      const message = 'Untimed text transcript: open with the text viewer, not subtitle cues';
      notify('danger', message);
      throw new Error(message);
    }
    if (!Array.isArray((doc as any).cues)) {
      notify( 'danger', 'Invalid JSON format: missing or invalid segments array',);
      throw new Error('Invalid JSON format: missing or invalid segments array');
    }

    // Contract-normalized cues: ragged segments derive text from words, default
    // missing timings to finite values, and keep stable string IDs. The shared
    // parser owns that normalization so UI and backend agree.
    const timed = doc as { cues: Array<{ id: string; start: number; end: number; text: string; speaker?: string; words?: WordEntry[] }> };
    const segments = (cuesToSrtEntries(timed.cues) as SrtEntry[]).sort((a, b) => a.startTime - b.startTime);
    return segments;
  }

  async unload(entries: SrtEntry[]): Promise<string> {
    const segments = entries.map((entry: SrtEntry, index: number) => ({
      id: entry.id,
      start: entry.startTime,
      end: entry.endTime,
      text: entry.text,
      words: entry.words || [],
      speaker: entry.speaker,
    }));

    return JSON.stringify({ segments }, null, 2);
  }

}
